import { Bone, Group, Matrix4, Mesh, Skeleton, SkinnedMesh } from "three";
import type { Material } from "three";
import { lampMaterial, setLamps } from "./lamps.js";
import type { LampState } from "./lamps.js";
import { paintMaterial, rimMaterial } from "./paint.js";
import type { RimStyle } from "./paint.js";
import { restOf } from "./template.js";
import type { BoneName, CarTemplate } from "./template.js";

/**
 * One drawn car of a template: its own bones (the moving parts) over the template's shared
 * geometry and materials (the paint per colour), plus its lamp panels. `apply` poses it from a
 * `CarPose`; nothing here allocates per frame.
 */

export interface CarPose {
  /** Wheel spin angle (rad) of the front and the rear wheels; steer angle of the fronts (rad, + = left). */
  spin: number;
  spinR: number;
  steer: number;
  /** Steering wheel turn (rad, + = clockwise from the driver). */
  wheel: number;
  /** Door opening 0..1. */
  door: number;
  /** Body attitude from the suspension (rad, m). */
  pitch: number;
  roll: number;
  lift: number;
  /** Per-wheel suspension travel (m, + = up): fl, fr, rl, rr. */
  travel: [number, number, number, number];
  lamps: LampState;
}

export const restPose = (): CarPose => ({
  spin: 0,
  spinR: 0,
  steer: 0,
  wheel: 0,
  door: 0,
  pitch: 0,
  roll: 0,
  lift: 0,
  travel: [0, 0, 0, 0],
  lamps: { head: 0, tail: 0, reverse: 0, indL: 0, indR: 0 },
});

const WHEELS: readonly BoneName[] = ["wheel_fl", "wheel_fr", "wheel_rl", "wheel_rr"];
/** Door swing at full opening (rad). */
export const DOOR_SWING = 1.12;

export class CarInstance {
  readonly root = new Group();
  private readonly bones: Bone[];
  private readonly byName = new Map<BoneName, Bone>();
  private readonly rest: { x: number; y: number; z: number }[];
  private readonly meshes: SkinnedMesh[] = [];
  private readonly lamps: Mesh;
  private readonly lampMat = lampMaterial();
  private color = "";
  private rim: RimStyle = "stock";
  private readonly doorSide: number;

  constructor(
    readonly template: CarTemplate,
    color: string,
    private readonly defaultColor: string,
    opts: { castShadow?: boolean } = {},
  ) {
    const { local, inverses } = restOf(template);
    this.bones = template.bones.map((b, i) => {
      const bone = new Bone();
      bone.name = b.name;
      bone.position.copy(local[i]!);
      this.byName.set(b.name, bone);
      return bone;
    });
    template.bones.forEach((b, i) => {
      if (b.parent >= 0) this.bones[b.parent]!.add(this.bones[i]!);
    });
    this.rest = local.map((v) => ({ x: v.x, y: v.y, z: v.z }));
    this.root.add(this.bones[0]!);
    this.root.updateMatrixWorld(true);
    const skeleton = new Skeleton(
      this.bones,
      inverses.map((m) => m.clone()),
    );
    for (const b of template.buckets) {
      const mesh = new SkinnedMesh(b.geometry, b.material);
      mesh.bind(skeleton, new Matrix4());
      mesh.castShadow = (opts.castShadow ?? false) && !b.transparent;
      mesh.receiveShadow = !b.transparent;
      mesh.boundingSphere = template.sphere.clone();
      mesh.userData["paint"] = b.paint;
      this.meshes.push(mesh);
      this.root.add(mesh);
    }
    const body = this.byName.get("body")!;
    this.lamps = new Mesh(template.lamps, this.lampMat);
    const bp = template.bones[1]!.pivot;
    this.lamps.position.set(-bp.x, -bp.y, -bp.z);
    this.lamps.frustumCulled = false;
    this.lamps.renderOrder = 2;
    this.lamps.visible = false;
    body.add(this.lamps);
    this.doorSide = template.dims.door?.side ?? 1;
    this.setColor(color);
  }

  /** Repaints (shared materials per colour). */
  setColor(hex: string): void {
    if (hex === this.color) return;
    this.color = hex;
    const t = this.template;
    const isDefault = hex.toLowerCase() === this.defaultColor.toLowerCase();
    t.buckets.forEach((b, i) => {
      if (b.paint) this.meshes[i]!.material = paintMaterial(b.material, t.paintRef, hex, isDefault);
    });
  }

  /** The rim finish (stock keeps the model's own). */
  setRim(style: RimStyle): void {
    if (style === this.rim) return;
    this.rim = style;
    this.template.buckets.forEach((b, i) => {
      if (b.wheel) this.meshes[i]!.material = rimMaterial(b.material, style);
    });
  }

  /** Every mesh of the car (picking, shadows). */
  get parts(): readonly SkinnedMesh[] {
    return this.meshes;
  }

  bone(name: BoneName): Bone | undefined {
    return this.byName.get(name);
  }

  apply(p: CarPose): void {
    for (let i = 0; i < 4; i++) {
      const b = this.byName.get(WHEELS[i]!);
      if (!b) continue;
      const r = this.rest[this.bones.indexOf(b)]!;
      b.position.y = r.y + p.travel[i]!;
      b.rotation.order = "YXZ";
      b.rotation.set(i < 2 ? p.spin : p.spinR, i < 2 ? p.steer : 0, 0);
    }
    for (const n of ["wheel_f", "wheel_r"] as const) {
      const b = this.byName.get(n);
      if (b) {
        b.rotation.order = "YXZ";
        b.rotation.set(n === "wheel_f" ? p.spin : p.spinR, n === "wheel_f" ? p.steer : 0, 0);
      }
    }
    const body = this.byName.get("body")!;
    const rb = this.rest[1]!;
    body.position.y = rb.y + p.lift;
    body.rotation.set(p.pitch, 0, p.roll);
    const door = this.byName.get("door_fl") ?? this.byName.get("door_fr");
    if (door) door.rotation.y = -this.doorSide * p.door * DOOR_SWING;
    const sw = this.byName.get("steering_wheel");
    if (sw) sw.rotation.z = p.wheel;
    const l = p.lamps;
    this.lamps.visible = l.head + l.tail + l.reverse + l.indL + l.indR > 0.01;
    setLamps(this.lampMat, l);
  }

  /** World position of a bone's rest point, e.g. the steering wheel for the driver's hands. */
  materials(): Material[] {
    return this.meshes.map((m) => m.material as Material);
  }

  dispose(): void {
    this.root.removeFromParent();
    this.lampMat.dispose();
    for (const m of this.meshes) m.skeleton.dispose();
  }
}
